import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { EEnrollmentStatus } from '@/shared/api/generated';
import {
  getApiV1CourseregistrationEnrollmentGridQueryKey,
  usePostApiV1CourseregistrationEnrollmentBatchStatusChange,
} from '@/shared/api/generated';

import type { EnrollmentStatus } from '../types/enrollment';
import { ENROLLMENT_STATUS_TO_CODE } from '../types/enrollmentStatusCode';
import type { BatchFormValues } from '../ui/templates/EnrollmentBatchStatusTemplate';

/**
 * Hook para alterar o status de múltiplas matrículas em lote.
 * Chama POST /api/v1/courseregistration/enrollment/batch-status-change.
 *
 * @param onSuccess - Callback chamado após alteração em lote bem-sucedida
 */
export function useBatchStatusChange(onSuccess: () => void) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('personManagement');

  const { mutate, isPending: isApplying } =
    usePostApiV1CourseregistrationEnrollmentBatchStatusChange({
      mutation: {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getApiV1CourseregistrationEnrollmentGridQueryKey(),
          });
          alert({ alertType: 'success', title: t('batchStatus.toast.applySuccess') });
          onSuccess();
        },
        onError: () => {
          alert({ alertType: 'warning', title: t('batchStatus.toast.applyError') });
        },
      },
    });

  const apply = useCallback(
    (data: BatchFormValues, classStudentId: number) => {
      if (!data.newStatus) return;
      mutate({
        data: {
          courseRegistrationsId: [...data.selectedIds],
          classStudentId,
          enrollmentStatus: ENROLLMENT_STATUS_TO_CODE[
            data.newStatus as EnrollmentStatus
          ] as EEnrollmentStatus,
          sendNotification: data.sendNotification,
          sendCertificateByEmail: data.sendCertificate,
          observation: data.observation || null,
          statusReasonId: null,
          subStatusReasonId: null,
        },
      });
    },
    [mutate],
  );

  return { apply, isApplying } as const;
}
